import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function FreeClaimsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [proposingLocation, setProposingLocation] = useState(null);
  const [locationData, setLocationData] = useState({ name: '', address: '', method: 'meet_in_person' });

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchClaims();
  }, [user]);

  const fetchClaims = async () => {
    try {
      const { data } = await API.get('/free/claims');
      setClaims(data.claims || []);
    } catch (err) {
      console.error('Failed to fetch claims:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const map = {
      pending: '⏳ Waiting for response',
      location_proposed: '📍 Propose location',
      location_agreed: '✓ Location confirmed',
      shipping_proposed: '🚚 Shipping proposed',
      shipping_agreed: '✓ Ready to exchange',
      completed: '✅ Completed',
      cancelled: '✕ Cancelled'
    };
    return map[status] || status;
  };

  const handleProposeLocation = async (claimId) => {
    if (!locationData.name.trim() || !locationData.address.trim()) {
      toast.error('Please fill in location details');
      return;
    }

    try {
      await API.patch(`/free/claims/${claimId}/propose-location`, {
        locationName: locationData.name,
        locationAddress: locationData.address,
        shippingMethod: locationData.method
      });
      toast.success('Location proposed!');
      setProposingLocation(null);
      setLocationData({ name: '', address: '', method: 'meet_in_person' });
      fetchClaims();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to propose location');
    }
  };

  const handleAgreeLocation = async (claimId) => {
    try {
      await API.patch(`/free/claims/${claimId}/agree-location`);
      toast.success('Location agreed!');
      fetchClaims();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to agree on location');
    }
  };

  const handleComplete = async (claimId) => {
    if (!window.confirm('Mark this claim as complete?')) return;
    try {
      await API.patch(`/free/claims/${claimId}/complete`);
      toast.success('Claim completed!');
      fetchClaims();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to complete claim');
    }
  };

  const handleCancel = async (claimId) => {
    if (!window.confirm('Cancel this claim?')) return;
    try {
      await API.patch(`/free/claims/${claimId}/cancel`);
      toast.success('Claim cancelled');
      fetchClaims();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel claim');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }} className="float-anim">🍒</div>
        <div style={{ fontSize: 14, color: 'var(--text-soft)' }}>Loading claims...</div>
      </div>
    );
  }

  if (!claims.length) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 40px', background: 'var(--cream)', borderRadius: 16 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>🆓</div>
        <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
          No active claims
        </h3>
        <p style={{ fontSize: 14, color: 'var(--text-soft)', marginBottom: 20 }}>
          Start claiming by browsing free items.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button className="btn btn-cherry" onClick={() => navigate('/free')}>
            🆓 Browse Free Items
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {claims.map(claim => (
        <div
          key={claim.id}
          style={{
            background: 'white',
            border: '1px solid var(--border)',
            borderRadius: 14,
            overflow: 'hidden'
          }}
        >
          {/* Summary */}
          <button
            onClick={() => setExpandedId(expandedId === claim.id ? null : claim.id)}
            style={{
              width: '100%',
              padding: '18px 20px',
              textAlign: 'left',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              gap: 16,
              alignItems: 'center',
              flexWrap: 'wrap'
            }}
          >
            {/* Item preview */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
                {claim.listing_title}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-soft)', marginBottom: 8 }}>
                {claim.owner_id === user.id
                  ? `Claimed by ${claim.claimer_first_name}`
                  : `From ${claim.owner_first_name}`}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
                Claimed {format(new Date(claim.created_at), 'MMM d, yyyy')}
              </div>
            </div>

            {/* Status badge */}
            <div style={{
              background: claim.status === 'completed' ? '#E8F5E9' : claim.status === 'cancelled' ? '#FFEBEE' : '#E8F5E9',
              color: claim.status === 'completed' ? '#2D7A3A' : claim.status === 'cancelled' ? '#C0392B' : '#2D7A3A',
              padding: '6px 12px',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 700,
              whiteSpace: 'nowrap'
            }}>
              {getStatusLabel(claim.status)}
            </div>

            {/* Expand icon */}
            <div style={{ fontSize: 16 }}>
              {expandedId === claim.id ? '▼' : '▶'}
            </div>
          </button>

          {/* Expanded details */}
          {expandedId === claim.id && (
            <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)', background: '#FAFAFA' }}>
              {/* Item info */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-soft)', marginBottom: 8, textTransform: 'uppercase' }}>
                  Item Details
                </div>
                <div style={{ background: 'white', padding: 12, borderRadius: 10, border: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{claim.listing_title}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-mid)', marginBottom: 8 }}>
                    {claim.listing_description}
                  </div>
                  {claim.listing_image && (
                    <img
                      src={`${API_BASE}${claim.listing_image}`}
                      alt=""
                      style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8 }}
                    />
                  )}
                </div>
              </div>

              {/* Owner/Claimer info */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-soft)', marginBottom: 8, textTransform: 'uppercase' }}>
                  {claim.owner_id === user.id ? 'Claimer' : 'Giver'}
                </div>
                <div style={{ background: 'white', padding: 12, borderRadius: 10, border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: '#E8F5E9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      fontWeight: 700,
                      color: '#2D7A3A',
                      flexShrink: 0
                    }}
                  >
                    {claim.owner_id === user.id
                      ? claim.claimer_first_name?.[0]?.toUpperCase()
                      : claim.owner_first_name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>
                      {claim.owner_id === user.id
                        ? `${claim.claimer_first_name} ${claim.claimer_last_name}`
                        : `${claim.owner_first_name} ${claim.owner_last_name}`}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-soft)' }}>
                      📍 {claim.owner_id === user.id ? claim.claimer_location : claim.owner_location}
                    </div>
                  </div>
                </div>
              </div>

              {/* Location section */}
              {claim.status !== 'cancelled' && claim.status !== 'completed' && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-soft)', marginBottom: 8, textTransform: 'uppercase' }}>
                    📍 Pickup Location
                  </div>

                  {claim.claim_location_name ? (
                    <div style={{ background: 'white', padding: 12, borderRadius: 10, border: '1px solid var(--border)', marginBottom: 12 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{claim.claim_location_name}</div>
                      <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>{claim.claim_location_address}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-soft)', marginTop: 4 }}>
                        {claim.owner_confirmed && claim.claimer_confirmed ? (
                          <span style={{ color: '#2D7A3A', fontWeight: 700 }}>✅ Both confirmed</span>
                        ) : (
                          <>
                            {claim.owner_confirmed && <span>✓ Giver confirmed · </span>}
                            {claim.claimer_confirmed && <span>✓ Claimer confirmed</span>}
                          </>
                        )}
                      </div>

                      {!claim.owner_confirmed && claim.owner_id === user.id && (
                        <button
                          onClick={() => handleAgreeLocation(claim.id)}
                          className="btn btn-green btn-sm"
                          style={{ marginTop: 8, background: '#4CAF50', color: 'white', border: 'none' }}
                        >
                          ✓ Confirm Location
                        </button>
                      )}

                      {!claim.claimer_confirmed && claim.claimer_id === user.id && (
                        <button
                          onClick={() => handleAgreeLocation(claim.id)}
                          className="btn btn-green btn-sm"
                          style={{ marginTop: 8, background: '#4CAF50', color: 'white', border: 'none' }}
                        >
                          ✓ Confirm Location
                        </button>
                      )}
                    </div>
                  ) : (
                    <div style={{ background: 'white', padding: 12, borderRadius: 10, border: '1px solid var(--border)' }}>
                      {proposingLocation === claim.id ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          <input
                            type="text"
                            placeholder="Location name (e.g., Starbucks on Main)"
                            value={locationData.name}
                            onChange={e => setLocationData(p => ({ ...p, name: e.target.value }))}
                            className="form-input"
                            style={{ fontSize: 13 }}
                          />
                          <textarea
                            placeholder="Address"
                            value={locationData.address}
                            onChange={e => setLocationData(p => ({ ...p, address: e.target.value }))}
                            className="form-textarea"
                            style={{ fontSize: 13, minHeight: 60 }}
                          />
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              onClick={() => handleProposeLocation(claim.id)}
                              className="btn btn-green btn-sm"
                              style={{ background: '#4CAF50', color: 'white', border: 'none' }}
                            >
                              📍 Propose
                            </button>
                            <button
                              onClick={() => setProposingLocation(null)}
                              className="btn btn-ghost btn-sm"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setProposingLocation(claim.id)}
                          className="btn btn-full"
                          style={{ background: '#4CAF50', color: 'white', border: 'none' }}
                        >
                          📍 Propose Pickup Location
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Action buttons */}
              {claim.status !== 'cancelled' && claim.status !== 'completed' && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {claim.owner_confirmed && claim.claimer_confirmed && (
                    <button
                      onClick={() => handleComplete(claim.id)}
                      className="btn btn-green btn-sm"
                      style={{ background: '#4CAF50', color: 'white', border: 'none' }}
                    >
                      ✅ Mark Complete
                    </button>
                  )}
                  <button
                    onClick={() => handleCancel(claim.id)}
                    className="btn btn-sm"
                    style={{ background: '#FFEBEE', color: '#C0392B', border: '1px solid rgba(192,57,43,.2)', borderRadius: 8, padding: '6px 14px', fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                  >
                    ✕ Cancel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
